import { useMemo } from 'react';
import ReactECharts from 'echarts-for-react';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import type { InfluencerPplCategoryGraph } from '@/utils/approval/influencerPpl';

type CategoryGraphProps = {
  graph: InfluencerPplCategoryGraph | undefined;
  /** 레거시: 작성 화면은 검색량 선만, 조회 화면은 노출건수 막대까지 */
  showExposure: boolean;
};

/** 카테고리별 검색량(%) 선 + 노출건수 막대 — 레거시 chart.js pplCalGraph */
const CategoryGraph = ({ graph, showExposure }: CategoryGraphProps) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const option = useMemo(() => {
    const searchLabel = t('검색량');
    const exposureLabel = t('노출건수');
    return {
      grid: { left: 40, right: 40, top: 40, bottom: 30 },
      legend: { top: 0 },
      tooltip: {
        trigger: 'axis',
        formatter: (
          params: { seriesName: string; value: number; axisValue: string }[],
        ) =>
          [
            params[0]?.axisValue ?? '',
            ...params.map((p) =>
              p.seriesName === searchLabel
                ? `${p.seriesName}: ${Number(p.value).toFixed(2)}%`
                : `${p.seriesName}: ${p.value}`,
            ),
          ].join('<br/>'),
      },
      xAxis: {
        type: 'category',
        data: graph?.date ?? [],
        axisTick: { show: false },
      },
      yAxis: [
        { type: 'value', minInterval: 1 },
        { type: 'value', position: 'right', splitLine: { show: false } },
      ],
      series: [
        {
          type: 'line',
          name: searchLabel,
          data: graph?.search ?? [],
          yAxisIndex: 0,
          color: theme.palette.primary.main,
          z: 2,
        },
        ...(showExposure
          ? [
              {
                type: 'bar',
                name: exposureLabel,
                data: graph?.graph ?? [],
                yAxisIndex: 1,
                barWidth: '40%',
                color: theme.palette.primary.light,
                itemStyle: { opacity: 0.4, borderRadius: 2 },
                z: 1,
              },
            ]
          : []),
      ],
    };
  }, [graph, showExposure, t, theme.palette.primary]);

  if (!graph) return null;
  return (
    <ReactECharts
      option={option}
      notMerge
      style={{ width: '100%', height: 320 }}
    />
  );
};

export default CategoryGraph;
